/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useTranslation } from 'react-i18next';
import { Text, UnstyledButton } from '@/shared/ui';
import { cn } from '@/shared/ui/utils/tailwind';
import { buildClientTabs, type ClientDetailTab, type ClientTabCounts } from './client-detail-tabs';

export interface ClientDetailTabBarProps {
  activeTab: ClientDetailTab;
  onTabChange: (tab: ClientDetailTab) => void;
  counts?: ClientTabCounts;
}

/**
 * Horizontal underline tablist for the client detail sections, in the
 * same shape the property sheet uses: one straight rule across the
 * strip, the active tab underlined in the seed colour, and each tab
 * carrying the count of what it holds.
 *
 * @param props - The active tab, the change handler, and per-tab counts.
 * @returns The rendered tab bar.
 */
export function ClientDetailTabBar({ activeTab, onTabChange, counts }: ClientDetailTabBarProps) {
  const { t } = useTranslation('features/clients');
  const tabs = buildClientTabs(t, counts);

  return (
    /*
      Sticky on a phone, so the tabs stay reachable through a long tab body:
      the history list runs well past the fold, and a tab bar you have to
      scroll back up to is a tab bar people stop using. The negative margins
      let it span the scrolling box's full width, as a bar must.
    */
    <div
      role="tablist"
      data-testid="client-detail-tab-bar"
      /*
        `shrink-0` is load-bearing. As a child of the scrolling flex column,
        a box that declares `overflow` will otherwise shrink to fit the
        column instead of overflowing it — this bar collapsed to its 1px
        bottom border, with nothing in the console to say so.
      */
      className="flex shrink-0 flex-nowrap items-center overflow-x-auto border-b border-(--border-subtle) scrollbar-none max-md:sticky max-md:top-0 max-md:z-10 max-md:-mx-(--space-5) max-md:min-h-11 max-md:bg-(--surface-primary) max-md:px-(--space-5)"
    >
      {tabs.map((tab) => {
        const selected = activeTab === tab.key;
        return (
          <UnstyledButton
            key={tab.key}
            type="button"
            role="tab"
            aria-selected={selected}
            data-testid={`tab-${tab.key}`}
            onClick={() => onTabChange(tab.key)}
            className={cn(
              'inline-flex shrink-0 items-center gap-[6px] whitespace-nowrap rounded-none border-0 border-b-2 border-solid bg-transparent px-(--space-4) py-(--space-3) text-[length:var(--text-body-sm)] cursor-pointer',
              selected
                ? '[font-weight:var(--font-semibold)] text-(--seed-base) border-b-(--seed-base)'
                : '[font-weight:var(--font-medium)] text-(--text-secondary) border-b-transparent hover:text-(--text-primary)',
            )}
          >
            {tab.label}
            {tab.count != null && (
              <Text
                component="span"
                className="inline-flex h-[17px] items-center rounded-(--radius-full) bg-(--surface-tertiary) px-[6px] text-[length:var(--text-meta)] [font-weight:var(--font-semibold)] tabular-nums text-(--text-tertiary)"
              >
                {tab.count}
              </Text>
            )}
          </UnstyledButton>
        );
      })}
    </div>
  );
}
